<template>
  <div v-if="isEmptyProduct && !isWebPlatform"
       :class="$style.emptyProduct">Coming Soon</div>
  <div v-else
       :class="$style.orderPage">
    <LoadingSpinner v-wait:visible="'preloadImage *'"/>
    <div :class="$style.orderPageMenu">
      <ConfiguratorCurrentBurger
        :configurator-category="currentCategory"
        :sorted-categories="productSortedCategories"/>
    </div>

    <div :class="$style.orderPageContainer">

      <div :class="$style.stepsMenu">
        <v-stepper v-model="currentCategory"
                   :class="$style.configurator">
          <v-stepper-header :class="$style.stepsList">
            <template v-for="(categoryKey, index) in allSortedCategories">
              <ConfiguratorStepTitle :last-step="false"
                                     :key="index + 'step'"
                                     :class="$style.step"
                                     :current-step="currentCategory"
                                     :category-key="categoryKey"
                                     :steps-amount="categoriesLength"
                                     @stepChange="currentPageChange"/>
              <v-divider :key="index+ 'stepDivider'"/>
            </template>
          </v-stepper-header>

          <v-stepper-items :class="$style.stepperItems">
            <div v-if="isEmptyProduct && isWebPlatform"
                 :class="$style.emptyProduct">Coming Soon</div>
            <div v-else
                 :class="$style.stepsContentWrapper">
              <template v-for="categoryKey in productSortedCategories">
                <ConfiguratorStepContent
                  v-if="categoryKey !== bossBundleCategoryId"
                  :category-key="categoryKey"
                  :key="`${categoryKey}-stepContent`"
                  :current-product="currentProduct"
                  :first="firstStepNoBundle(categoryKey)"
                  :last="!hasBundle &&
                    categoryKey ===
                  productSortedCategories[productSortedCategories.length - 1]"
                  :current-step="currentCategory"
                  :lock-next-step="lockNextStep"
                  @stepChange="currentPageChange"/>
              </template>

              <ConfiguratorStepBundleDialog v-if="!kidsMenu"
                                            @stepChange="currentPageChange"/>

              <template v-for="categoryKey in bundleSortedCategories">
                <ConfiguratorStepBundleContent
                  :order-delta="currentProduct.categories.length - 1"
                  :bundle-product="bundleProduct"
                  :current-product="currentProduct"
                  :bundle-id="bossBundleId"
                  :category-key="categoryKey"
                  :key="`${categoryKey}-stepContent`"
                  :first="false"
                  :last="lastStepBundle(categoryKey)"
                  :current-step="currentCategory"
                  :lock-next-step="lockNextStep"
                  @stepChange="currentPageChange"/>
              </template>

            </div>
          </v-stepper-items>
        </v-stepper>
      </div>
    </div>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { Getter, Action } from 'vuex-class';
import { Watch } from 'vue-property-decorator';
import { CategoryWizard } from '@/apps/menu/constants';
import ConfiguratorStepTitle from '@/apps/configurator/components/ConfiguratorStepTitle';
import ConfiguratorStepContent from '@/apps/configurator/components/ConfiguratorStepContent';
import ConfiguratorStepBundleContent from '@/apps/configurator/components/ConfiguratorStepBundleContent';
import ConfiguratorStepBundleDialog from '@/apps/configurator/components/ConfiguratorStepBundleDialog';
import ConfiguratorCurrentBurger from '@/apps/configurator/components/ConfiguratorCurrentBurger';
import LoadingSpinner from '@/apps/configurator/components/LoadingSpinner';
import { CART_ITEM_REMOVE } from '@/apps/cart/store/types';
import { CONFIGURATOR_CLEAR } from '../store/types';
import { PRODUCTS_ID, CATEGORY_ID } from '../../../config/constants';

/**
 * Page for burger configurator component. Provide ui for dish composition.
 *
 * Data:
 * @property {number} currentCategory - define current step value.
 */
@Component({
  components: {
    LoadingSpinner,
    ConfiguratorStepTitle,
    ConfiguratorStepContent,
    ConfiguratorStepBundleContent,
    ConfiguratorCurrentBurger,
    ConfiguratorStepBundleDialog,
  },
  props: {
    changeCategory: {
      type:     Number,
      default:  null,
      required: false,
    },
  },
})
export default class Configurator extends Vue {
  currentCategory = 1;
  nextStepDebounce = 600;
  nextStepDebounceTimer = null;

  /**
   * Retrieves key for product configurator.
   */
  @Getter currentProductKey;
  @Getter isWebPlatform;

  /**
   * Retrieves products from store by key.
   */
  @Getter productByKey;
  @Getter products;

  @Getter categories;
  @Getter categoryByKey;
  @Getter orderItemCompleted;
  @Getter orderItemId;

  @Action(CART_ITEM_REMOVE) removeOrderItem;
  @Action(CONFIGURATOR_CLEAR) clearConfigurator;

  /**
   * If restore product from cart with open configurator.
   */
  @Watch('orderItemId')
  hotUpdateProduct() {
    this.currentCategory = 1;
  }

  get lockNextStep() {
    return !!this.nextStepDebounceTimer;
  }

  setLockTimeout() {
    this.nextStepDebounceTimer = setTimeout(() => {
      clearTimeout(this.nextStepDebounceTimer);
      this.nextStepDebounceTimer = null;
    }, this.nextStepDebounce);
  }

  /**
   * Trigger navigation between steps.
   * @param {number} newCurrentSate - Step number to be navigated to.
   */
  currentPageChange(newCurrentSate) {
    const shouldSkipBundleDialog =
      this.currentProductKey === PRODUCTS_ID.BOSS_BURGER &&
      this.currentCategory === 8 && newCurrentSate === 7;

    if (shouldSkipBundleDialog) {
      this.currentCategory = 6;
    } else {
      this.currentCategory = newCurrentSate;
    }
    this.setLockTimeout();
  }

  /**
   * Check passed step number is the last one
   * @param categoryKey - step number;
   * @returns {boolean}
   */
  lastStepBundle(categoryKey) {
    return (
      this.categories[categoryKey].orderIndex ===
      this.bundleProduct.categories.length
    );
  }

  /**
   * Current product has no categories to configure.
   */
  get isEmptyProduct() {
    return this.currentProduct.categories.length === 0;
  }

  /**
   * Check this product has bundle.
   */
  get hasBundle() {
    return this.bundleProduct.categories.length !== 0;
  }

  /**
   * Check passed step number is the first one.
   * @param categoryKey
   * @returns {boolean}
   */
  firstStepNoBundle(categoryKey) {
    return this.categories[categoryKey].orderIndex === 1;
  }

  /**
   * Retrieves product data from store.
   * @returns {{
   *   name: string,
   *   showInMenu: boolean,
   *   orderIndex: number,
   *   categories: [any],
   * }}
   */
  get currentProduct() {
    return this.productByKey(this.currentProductKey);
  }

  get kidsMenu() {
    return this.currentProductKey === PRODUCTS_ID.KIDS_BURGER
      || this.currentProductKey === PRODUCTS_ID.TREASURE_BOX
      || this.currentProductKey === PRODUCTS_ID.GRILLED_CHEESE;
  }

  get bundleSortedCategories() {
    if (this.currentProductKey === PRODUCTS_ID.KIDS_BURGER
      || this.currentProductKey === PRODUCTS_ID.TREASURE_BOX
      || this.currentProductKey === PRODUCTS_ID.GRILLED_CHEESE) {
      return this.sortedCategories(this.bundleProduct);
    }

    return [ 71, ...this.sortedCategories(this.bundleProduct) ];
  }

  get productSortedCategories() {
    return this.sortedCategories(this.currentProduct);
  }

  get allSortedCategories() {
    if (this.currentProductKey === PRODUCTS_ID.KIDS_BURGER
      || this.currentProductKey === PRODUCTS_ID.TREASURE_BOX
      || this.currentProductKey === PRODUCTS_ID.GRILLED_CHEESE) {
      return this.productSortedCategories;
    }

    const categoriesToExclude = [
      CATEGORY_ID.BURGER_BUNDLE,
      CATEGORY_ID.BUNDLE_SAUCES,
      CATEGORY_ID.BOWL_BUNDLE,
    ];

    return [
      ...this.productSortedCategories,
      ...this.bundleSortedCategories,
    ].filter(category => !categoriesToExclude.includes(category));
  }

  get categoriesLength() {
    if (this.currentProductKey === PRODUCTS_ID.KIDS_BURGER
      || this.currentProductKey === PRODUCTS_ID.TREASURE_BOX
      || this.currentProductKey === PRODUCTS_ID.GRILLED_CHEESE) {
      return this.bundleSortedCategories.length - 1;
    }

    const bundleCategoriesLength = this.bundleSortedCategories.length;

    return this.productSortedCategories.length + bundleCategoriesLength;
  }

  /**
   * Sort categories by order index.
   */
  sortedCategories(product) {
    return product.categories
      .sort((a, b) => {
        const categoryA = this.categoryByKey(a);
        const categoryB = this.categoryByKey(b);

        return categoryA.orderIndex - categoryB.orderIndex;
      });
  }

  /**
   * Get Id of boss bundle category
   */
  get bossBundleCategoryId() {
    let bossBundleCategoryId = null;

    this.currentProduct.categories.forEach((categoryId) => {
      if (
        this.categories[categoryId].wizardType === CategoryWizard.CHILD_WIZARD
      ) {
        bossBundleCategoryId = categoryId;
      }
    });

    return bossBundleCategoryId;
  }

  /**
   * Get Id of boss bundle product
   */
  get bossBundleId() {
    if (this.bossBundleCategoryId) {
      return this.categories[this.bossBundleCategoryId].subProductId;
    }

    return 0;
  }

  /**
   * Get boss bundle product
   */
  get bundleProduct() {
    if (!this.bossBundleId) {
      return { categories: [] };
    }

    return this.products[this.bossBundleId];
  }

  created() {
    window.addEventListener('keydown', (e) => {
      if (e.key === ' ' && e.shiftKey) {
        this.currentPageChange(this.currentCategory + 1);
      }
    });

    /**
     * Set current category
     */
    if (this.$route.params.currentCategoryKey) {
      const index = this.productSortedCategories
        .findIndex(el => el === this.$route.params.currentCategoryKey);

      this.currentCategory = index !== -1 ? index + 1 : 1;
    }
  }

  /**
   * Vue destroyed hook.
   */
  destroyed() {
    if (!this.orderItemCompleted) {
      this.removeOrderItem(this.orderItemId);
    }
    this.clearConfigurator();
  }
}
</script>

<style lang="scss" module>
@import "~@/style/variables.scss";

$steps-menu-border: #EEE;
$steps-border: #E1E1E1;

.emptyProduct {
  display: flex;
  justify-content: center;
  font-size: 4vh;
  margin-top: 2vh;
}

.orderPage {
  background-color: $white;
  min-height: 100%;
  max-height: 100%;
  display: flex;
}

.stepsContentWrapper {
  position: relative;
}

.configurator {
  flex: 1 1 auto;
  box-shadow: none;
}

.orderPageMenu {
  width: $asideMenuWidth;
  display: flex;
}

.orderPageContainer {
  width: calc(100% - #{$asideMenuWidth});
  display: flex;
  flex: 1 1 auto;
}

.orderStepsMenu {
  display: flex;
}

.stepsMenu {
  border: 1px solid $steps-menu-border;
  border-top: none;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.stepperItems {
  position: static;
}

.stepsList {
  list-style: none;
  padding-left: 0;
  margin: 0;
  height: $stepsMenuHeight;
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  box-shadow: none;
  border-bottom: 1px solid $steps-border;
}

@media screen and (max-width: $kiosk-breakpoint-max) {
  .stepsList {
    height: $stepsMenuKiosk;
  }

  .stepperItems {
    max-height: calc(100% - #{$stepsMenuKiosk});
    overflow: auto;
  }

  .orderPageMenu {
    width: $asideMenuWide;
    margin-top: $stepsMenuKiosk;
    z-index: 5;
  }
  .orderPageContainer {
    width: 100%;
    margin-left: -$asideMenuWide;
  }
}

@media screen and (max-width: 1020px) {
  .stepsList {
    height: $stepsMenuHeight;
  }

  .orderPageMenu {
    width: $asideMenuWidth;
    margin-top: 60px;
  }
  .orderPageContainer {
    width: 100%;
    margin-left: -$asideMenuWidth;
  }
}

@media screen and (max-width: 959px) {
  .stepsMenu .configurator .stepsList .step > span + div {
    display: flex;
  }
}

@media screen and (max-width: 940px) {
  .orderPageMenu {
    display: none;
  }
  .orderPageContainer {
    margin-left: 0;
  }
}

@media screen and (max-width: $mobile-breakpoint-max) {
  .stepsList {
    height: $stepsMenuHeightSmall;
    flex-shrink: 0;
  }

  .orderPage {
    overflow: hidden;
    flex: auto;
    height: auto;
  }

  .configurator {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .stepsContentWrapper,
  .stepperItems {
    max-height: 100%;
    flex: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/configurator/components/Configurator.vue